import { Link, useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '../store/auth.ts';
import { Icon } from './Icon.tsx';

type Dest = { to: string; label: string; icon: string; matchPrefix?: string };

function navFor(role: 'SYS_ADMIN' | 'ORG_ADMIN' | 'USER'): Dest[] {
  if (role === 'SYS_ADMIN') {
    return [
      { to: '/admin/orgs', label: 'Orgs', icon: 'apartment', matchPrefix: '/admin/orgs' },
      { to: '/admin/subscriptions', label: 'Subs', icon: 'credit_card', matchPrefix: '/admin/subscriptions' },
      { to: '/profile', label: 'Profile', icon: 'person' },
    ];
  }
  return [
    { to: '/dashboard', label: 'Home', icon: 'home', matchPrefix: '/dashboard' },
    ...(role === 'ORG_ADMIN' ? [{ to: '/team', label: 'Team', icon: 'group', matchPrefix: '/team' }] : []),
    { to: '/profile', label: 'Profile', icon: 'person' },
  ];
}

// Material 3 navigation bar (mobile). Destinations only — sign-out and the
// theme toggle live in the mobile top bar (Header), matching WapSign. The
// active destination shows a tonal pill indicator behind a filled icon, with
// the label below.
export function BottomNav(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (!user) return null;
  const items = navFor(user.role);

  return (
    <nav
      aria-label="Primary"
      className="md:hidden fixed inset-x-0 bottom-0 z-40 flex items-stretch bg-[hsl(var(--chrome))]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {items.map((item) => {
        const active = item.matchPrefix ? pathname.startsWith(item.matchPrefix) : pathname === item.to;
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-label={item.label}
            aria-current={active ? 'page' : undefined}
            className="flex flex-1 flex-col items-center gap-1 pt-3 pb-2 active:scale-95 transition-transform"
          >
            <span
              className={[
                'relative grid h-8 w-16 place-items-center rounded-full transition-colors',
                active
                  ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]'
                  : 'text-muted-foreground',
              ].join(' ')}
            >
              <Icon name={item.icon} size={24} fill={active} />
            </span>
            <span className={`text-[11px] leading-none ${active ? 'font-semibold text-ink' : 'text-muted-foreground'}`}>
              {item.label}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
